// ./src/components/features/EventMap/mapHelpers/mapHelpers.ts
//
// Small pure helpers shared by the Event Map board, management page and public
// widget: category-meta lookup shape, coordinate guards, a default marker color,
// and a Google Maps directions URL.

import { EventMapElement, MapPoint } from "@/utils/api";

export interface MapCategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

/** Fallback marker color when an element has no category (or the category has no color). */
export const DEFAULT_MARKER_COLOR = "#e8590c"; // coral, matches the brand accent

/** True when the element carries a usable geo pin. */
export const hasCoords = (el: Pick<EventMapElement, "latitude" | "longitude">): boolean =>
  typeof el.latitude === "number" && typeof el.longitude === "number";

/** The marker color for an element (category color, else the default). */
export const markerColorOf = (meta: MapCategoryMeta | null): string =>
  meta?.color || DEFAULT_MARKER_COLOR;

/** First letter of the element name (marker glyph), uppercased; "•" when empty. */
export const markerGlyph = (name?: string | null): string => {
  const trimmed = (name || "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "•";
};

/** "Open in Google Maps" URL for an element's pin. */
export const buildElementMapsUrl = (el: Pick<EventMapElement, "latitude" | "longitude">): string | null => {
  if (!hasCoords(el)) {
    return null;
  }

  return `https://www.google.com/maps?q=${el.latitude},${el.longitude}`;
};

// ============================================================================
// Zone geometry — geodesic measurements computed on the client (no extra Google
// library load). Matches google.maps.geometry.spherical closely enough for
// event-scale areas.
// ============================================================================

const EARTH_RADIUS_M = 6378137;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance between two points, in meters (haversine). */
export const distanceMeters = (a: MapPoint, b: MapPoint): number => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Length of each edge of the closed polygon (last vertex → first), in meters. */
export const edgeLengthsMeters = (points: MapPoint[]): number[] => {
  if (!points || points.length < 2) {
    return [];
  }
  const out: number[] = [];
  for (let i = 0; i < points.length; i += 1) {
    out.push(distanceMeters(points[i], points[(i + 1) % points.length]));
  }
  return out;
};

/** Total perimeter of the closed polygon, in meters. */
export const polygonPerimeterMeters = (points: MapPoint[]): number =>
  edgeLengthsMeters(points).reduce((sum, d) => sum + d, 0);

/**
 * Spherical polygon area in m² (the formula google.maps.geometry.spherical.
 * computeArea uses). Returns 0 for < 3 vertices.
 */
export const polygonAreaSqm = (points: MapPoint[]): number => {
  if (!points || points.length < 3) {
    return 0;
  }
  let total = 0;
  for (let i = 0; i < points.length; i += 1) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    total += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
};

/** Human area: m² → ha → km², plus acres in parentheses. */
export const formatArea = (sqm: number): string => {
  if (!sqm || sqm <= 0) {
    return "—";
  }
  const acres = sqm / 4046.8564224;
  let metric: string;
  if (sqm < 10000) {
    metric = `${Math.round(sqm).toLocaleString()} m²`;
  } else if (sqm < 1000000) {
    metric = `${(sqm / 10000).toFixed(2)} ha`;
  } else {
    metric = `${(sqm / 1000000).toFixed(2)} km²`;
  }
  return `${metric} (${acres < 10 ? acres.toFixed(2) : Math.round(acres).toLocaleString()} ac)`;
};

/** Human length: m → km, plus feet/miles in parentheses. */
export const formatLength = (m: number): string => {
  if (!m || m <= 0) {
    return "—";
  }
  const feet = m * 3.280839895;
  const metric = m < 1000 ? `${Math.round(m).toLocaleString()} m` : `${(m / 1000).toFixed(2)} km`;
  const imperial = feet < 1000 ? `${Math.round(feet).toLocaleString()} ft` : `${(feet / 5280).toFixed(2)} mi`;
  return `${metric} (${imperial})`;
};
